/* eslint-disable max-len */
import { getSecondary } from '@brands/Utils/getPrimaryColor';
import { Option, OptionLanguage } from '@brands/Utils/selectOptions';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, Checkbox, ListItemText, Menu, MenuItem, styled } from '@mui/material';
import * as React from 'react';
import { MultiValue } from 'react-select';

const StyledMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    borderRadius: 8,
    width: 300,
    maxHeight: 300,
    overflowY: 'auto',
    boxShadow:
      'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '8px 0',
    },
  },
  '& .MuiCheckbox-root': {
    color: getSecondary(),
    '&.Mui-checked': {
      color: getSecondary(),
      borderRadius: 4,
    },
  },
}));

interface LanguageMultiSelectProps {
  selectedLanguages: MultiValue<Option>;
  setSelectedLanguages: React.Dispatch<React.SetStateAction<MultiValue<Option>>>;
  languageOptions: OptionLanguage[];
}

const LanguageMultiSelect = ({
  selectedLanguages,
  setSelectedLanguages,
  languageOptions,
}: LanguageMultiSelectProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleToggle = (option: Option): void => {
    const exists = selectedLanguages.some((item) => item.value === option.value);
    if (exists) {
      setSelectedLanguages((prev) => prev.filter((item) => item.value !== option.value));
    } else {
      setSelectedLanguages((prev) => [...prev, option]);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const isSelected = (option: Option): boolean => selectedLanguages.some((item) => item.value === option.value);

  const selectedLabels = selectedLanguages.map((opt) => opt.label).join(', ') || 'Select Language';

  return (
    <div>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          borderRadius: '24px',
          textTransform: 'none',
          fontWeight: 'bold',
          color: getSecondary(),
          borderColor: getSecondary(),
          px: 2,
          '&:hover': {
            borderColor: getSecondary(),
            backgroundColor: 'transparent',
          },
        }}
      >
        {selectedLabels}
      </Button>
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {languageOptions.map((option) => (
          <MenuItem key={option.value} onClick={() => handleToggle(option)}>
            <Checkbox checked={isSelected(option)} tabIndex={-1} disableRipple sx={{ padding: 0.5, marginRight: 1 }} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </StyledMenu>
    </div>
  );
};

export default LanguageMultiSelect;
